import { Component, OnInit, OnDestroy, DestroyRef, NgZone, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Tooltip } from 'primeng/tooltip';
import { Tag } from 'primeng/tag';
import { catchError, of, forkJoin } from 'rxjs';

import { PresalesService } from '../../services/presales.service';
import { ClientService } from '../../services/client.service';
import { ServiceGroupService } from '../../services/service-group.service';
import {
  PresalesCostCenter,
  PresalesProject,
  CreatePresalesProject,
  SalesStage,
  PresalesProgress,
  PresalesProjectStatus,
  Currency,
  CurrencyRate,
  SALES_STAGES,
  PRESALES_PROGRESS_OPTIONS,
  presalesProgressLabel,
  PRESALES_PROJECT_STATUS_OPTIONS,
  CURRENCIES,
  LEAD_SOURCES,
  PromoteToDeliveryRequest,
  buildRateMap,
  toUsd,
} from '../../models/presales.model';
import { Client } from '../../models/client.model';
import { ServiceGroup } from '../../models/service-group.model';
import { MessageService } from 'primeng/api';

import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { AuthService } from '../../services/auth.service';

// Tab components shown inside the details modal (Activities / RAID).
import { PresalesActivityComponent } from '../presales-activity/presales-activity.component';
import { PresalesRaidComponent } from '../presales-raid/presales-raid.component';
import { TeamAllocationMonthComponent } from '../team-allocation-month/team-allocation-month.component';

interface Option<T = string> { label: string; value: T; }

/**
 * A project plus the fields this page derives for the table. `overdueDays` is
 * computed on load rather than in the template so the Overdue column can be
 * sorted like any other field (p-table sorts on row properties, not expressions).
 */
type PresalesProjectRow = PresalesProject & {
  /** Whole days past `endDate`, or null when not set / not yet due. */
  overdueDays: number | null;
};

/** Table (default), scannable cards, or a stage-column Kanban board. */
type ViewMode = 'table' | 'grid' | 'kanban';

/**
 * One Kanban column — the deals sitting at a single progress state.
 * `key` is '' for the catch-all column holding deals with no progress set.
 */
interface KanbanColumn {
  key: PresalesProgress | '';
  label: string;
  cssClass: string;   // progress-to-start / progress-inprogress / …
  projects: PresalesProjectRow[];
}

/**
 * Presales Project Details page — read-only comprehensive list.
 * Row click opens a modal with the full data (no editing here — that lives on Inventory).
 *
 * Two viewing modes:
 *   - Table (default): every column visible, sortable
 *   - Grid (cards):    scannable per-project cards
 */
@Component({
  selector: 'app-presales-project-details',
  standalone: true,
  imports: [
    CommonModule, FormsModule, TableModule, Button, Dialog, Select,
    InputText, Tooltip, Tag,
    PresalesActivityComponent,
    PresalesRaidComponent,
    TeamAllocationMonthComponent,
  ],
  providers: [DatePipe],
  templateUrl: './presales-project-details.component.html',
  styleUrl: './presales-project-details.component.scss'
})
export class PresalesProjectDetailsComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  projects: PresalesProjectRow[] = [];

  /** USD conversion rates (currency → USD-per-unit). Empty until loaded. */
  rateMap = new Map<Currency, number>();
  filteredProjects: PresalesProjectRow[] = [];
  loading = true;

  // Filters
  searchText = '';
  selectedProjectStatus: PresalesProjectStatus | null = null;
  selectedStage: SalesStage | null = null;
  selectedProgress: PresalesProgress | null = null;
  selectedPMId: string | null = null;
  selectedClientId: string | null = null;
  selectedAccountManagerId: string | null = null;
  selectedServiceGroupId: string | null = null;

  // View mode
  viewMode: ViewMode = 'table';

  /** Progress columns for the Kanban board; rebuilt whenever filters change. */
  kanbanColumns: KanbanColumn[] = [];

  /**
   * Columns rendered on the board. Every progress state is always shown: an
   * empty "On Hold" column is itself a finding, and a board whose columns come
   * and go is harder to read than one with a stable shape.
   */
  get visibleKanbanColumns(): KanbanColumn[] {
    return this.kanbanColumns;
  }

  stageOptions: Option<SalesStage>[] = SALES_STAGES;
  progressOptions: Option<PresalesProgress>[] = PRESALES_PROGRESS_OPTIONS;

  /** Spaces 'InProgress' for display; stored values are left untouched. */
  progressLabel = presalesProgressLabel;
  projectStatusOptions: Option<PresalesProjectStatus>[] = PRESALES_PROJECT_STATUS_OPTIONS;
  currencyOptions = CURRENCIES;

  // Derived from loaded projects — no extra API calls for the read-only Details page
  pmOptions: { id: string; name: string }[] = [];
  clientOptions: { id: string; name: string }[] = [];
  accountManagerOptions: { id: string; name: string }[] = [];
  serviceGroupOptions: { id: string; name: string }[] = [];

  // Full master lists for the inline System-Fields editors (loaded from their own endpoints,
  // not derived from projects — a project can be assigned a group/cost-center it doesn't yet have).
  editServiceGroupOptions: { id: string; name: string }[] = [];
  costCenterOptions: { id: string; name: string }[] = [];
  leadSourceOptions: Option[] = LEAD_SOURCES.map(s => ({ label: s, value: s }));

  // Details modal
  displayDialog = false;
  selectedProject: PresalesProject | null = null;
  activeTab: 'overview' | 'activities' | 'raid' | 'team-allocation' = 'overview';

  // ---- Inline edit (Overview tab) — Admin / PM only ----
  /** True for Admins or PMs; gates the Edit button. */
  canManage = false;
  /** Whether the Overview tab is currently in edit mode. */
  editMode = false;
  /** Working copy edited in the form; committed to selectedProject on Save. */
  editModel: PresalesProject | null = null;
  /** True while an update request is in flight — blocks double-submit. */
  saveInFlight = false;

  // Promote-to-Delivery wizard
  /**
   * Master switch for the Promote-to-Delivery feature in the UI. See the matching flag on
   * PresalesProjectInventoryComponent for why it is off: promotion stamps the delivery cost
   * center onto the twin Project, which the allocation export then reads, so promoted
   * pursuits reported a cost center that no longer matched the presales record.
   *
   * Display of PAST promotions stays visible — only the action is withdrawn.
   */
  readonly promoteEnabled = false;

  displayPromoteDialog = false;
  promoteMode: 'create' | 'link' = 'create';
  promoteExistingClientId: string | null = null;
  promoteProjectNameOverride = '';
  promoteProjectCodeOverride = '';
  promoteInFlight = false;
  mainClients: Client[] = [];
  /** Wizard current step (1 = choose client, 2 = project details, 3 = review). */
  promoteStep: 1 | 2 | 3 = 1;
  readonly promoteSteps: { n: 1 | 2 | 3; title: string }[] = [
    { n: 1, title: 'Client' },
    { n: 2, title: 'Details' },
    { n: 3, title: 'Review' },
  ];

  constructor(
    private presalesService: PresalesService,
    private clientService: ClientService,
    private serviceGroupService: ServiceGroupService,
    private messageService: MessageService,
    public pageHeaderService: PageHeaderSearchActionService,
    private datePipe: DatePipe,
    private ngZone: NgZone,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    // Handoff from the Consolidated Board: /presales/details?projectId=…&openForm=1.
    // Read in the constructor so the flags are set before load() resolves.
    const qp = this.route.snapshot.queryParamMap;
    this.pendingProjectId = qp.get('projectId');
    this.pendingOpenForm = qp.get('openForm') === '1';
  }

  /** Set by the ?projectId=…&openForm=1 handoff; consumed once after the first load. */
  private pendingProjectId: string | null = null;
  private pendingOpenForm = false;

  /**
   * Completes the handoff: opens the details dialog on the requested project.
   *
   * Deferred until the projects have loaded, because the dialog is populated from the
   * loaded row. Consumed once and cleared either way, so a later filter change cannot
   * reopen a stale project.
   */
  private consumePendingProjectHandoff(): void {
    if (!this.pendingOpenForm || !this.pendingProjectId) return;

    const projectId = this.pendingProjectId;
    this.pendingOpenForm = false;
    this.pendingProjectId = null;

    const project = this.projects.find(p => p.id === projectId);
    if (!project) {
      // Deleted or deactivated between the two screens. Saying so beats opening nothing
      // and looking like a dead click.
      this.messageService.add({
        severity: 'warn',
        summary: 'Project unavailable',
        detail: 'That presales project could not be found.'
      });
      return;
    }

    this.openDetails(project);
  }

  ngOnInit(): void {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search projects, clients, PMs…');
    this.pageHeaderService.setCanShowPrimaryAction(false);   // no Add button on Details
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);

    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(v => { this.searchText = v ?? ''; this.applyFilters(); });

    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.ngZone.run(() => this.exportToExcel()));

    // Admins and PMs may edit Overview fields inline (same rule as Inventory).
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.canManage = !!(user?.isAdmin || user?.isPM);
    });

    this.load();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  load(): void {
    this.loading = true;
    forkJoin({
      projects:      this.presalesService.getAllProjects().pipe(catchError(() => of([] as PresalesProject[]))),
      rates:         this.presalesService.getCurrencyRates().pipe(catchError(() => of([] as CurrencyRate[]))),
      serviceGroups: this.serviceGroupService.getAllServiceGroups().pipe(catchError(() => of([] as ServiceGroup[]))),
      costCenters:   this.presalesService.getAllCostCenters().pipe(catchError(() => of([] as PresalesCostCenter[]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.projects = (data.projects || []).map(p => ({
          ...p,
          overdueDays: this.calcOverdueDays(p.endDate),
        }));
        this.rateMap = buildRateMap(data.rates);
        this.editServiceGroupOptions = (data.serviceGroups || [])
          .filter(sg => sg.isActive)
          .map(sg => ({ id: sg.id, name: sg.name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        this.costCenterOptions = (data.costCenters || [])
          .filter(cc => cc.isActive)
          .map(cc => ({ id: cc.id, name: cc.name }))
          .sort((a, b) => a.name.localeCompare(b.name));
        this.rebuildFilterOptions();
        this.applyFilters();
        this.loading = false;

        // Deferred until here: the details dialog is populated from the loaded row.
        this.consumePendingProjectHandoff();
      });
  }

  /** Build dropdown options from the values that actually appear in the loaded projects. */
  private rebuildFilterOptions(): void {
    const uniq = (rows: { id: string; name: string }[]) => {
      const seen = new Set<string>();
      return rows
        .filter(r => r.id && !seen.has(r.id) && (seen.add(r.id), true))
        .sort((a, b) => a.name.localeCompare(b.name));
    };
    this.pmOptions = uniq(
      this.projects.filter(p => p.managerId).map(p => ({ id: p.managerId!, name: p.managerName ?? '—' }))
    );
    this.clientOptions = uniq(
      this.projects.filter(p => p.presalesClientId).map(p => ({ id: p.presalesClientId!, name: p.presalesClientName ?? '—' }))
    );
    this.accountManagerOptions = uniq(
      this.projects.filter(p => p.accountManagerId).map(p => ({ id: p.accountManagerId!, name: p.accountManagerName ?? '—' }))
    );
    this.serviceGroupOptions = uniq(
      this.projects.filter(p => p.serviceGroupId).map(p => ({ id: p.serviceGroupId!, name: p.serviceGroupName ?? '—' }))
    );
  }

  // ================= Filters =================

  applyFilters(): void {
    // This page only ever lists active pursuits — inactive ones are not shown at all,
    // so there is no active/inactive filter to choose from.
    let list = this.projects.filter(p => p.isActive !== false);
    const s = this.searchText.trim().toLowerCase();
    if (s) {
      list = list.filter(p =>
        p.name?.toLowerCase().includes(s) ||
        p.projectCode?.toLowerCase().includes(s) ||
        p.presalesClientName?.toLowerCase().includes(s) ||
        p.managerName?.toLowerCase().includes(s) ||
        p.accountManagerName?.toLowerCase().includes(s)
      );
    }
    if (this.selectedProjectStatus) {
      list = list.filter(p => p.projectStatus === this.selectedProjectStatus);
    }
    if (this.selectedStage) {
      list = list.filter(p => p.salesStage === this.selectedStage);
    }
    if (this.selectedProgress) {
      list = list.filter(p => p.progress === this.selectedProgress);
    }
    if (this.selectedPMId) {
      list = list.filter(p => p.managerId === this.selectedPMId);
    }
    if (this.selectedClientId) {
      list = list.filter(p => p.presalesClientId === this.selectedClientId);
    }
    if (this.selectedAccountManagerId) {
      list = list.filter(p => p.accountManagerId === this.selectedAccountManagerId);
    }
    if (this.selectedServiceGroupId) {
      list = list.filter(p => p.serviceGroupId === this.selectedServiceGroupId);
    }
    this.filteredProjects = list;
    // Keep the board in step with the filters while it is the active view.
    if (this.viewMode === 'kanban') this.rebuildKanbanColumns();
    this.pageHeaderService.setExportDisabled(this.filteredProjects.length === 0);
  }

  hasActiveFilters(): boolean {
    return !!(
      this.selectedProjectStatus ||
      this.selectedStage ||
      this.selectedProgress ||
      this.selectedPMId ||
      this.selectedClientId ||
      this.selectedAccountManagerId ||
      this.selectedServiceGroupId
    );
  }

  exportToExcel(): void {
    if (!this.filteredProjects.length) return;
    const rows = this.filteredProjects.map(p => ({
      'Code':              p.projectCode || '',
      'Project Name':      p.name,
      'Client':            p.presalesClientName || '',
      'Stage':             p.salesStage,
      'Progress':          p.progress || '',
      'Status':            p.projectStatus || '',
      'Winning %':         p.winningPercentage,
      'Currency':          p.currency,
      'Revenue Target':    p.revenueTarget,
      'PM':                p.managerName || '',
      'Sales Owner':       p.accountManagerName || '',
      'Service Group':     p.serviceGroupName || '',
      'Cost Center':       p.costCenterName || '',
      'Start Date':        this.formatDate(p.startDate),
      'End Date':          this.formatDate(p.endDate),
      'Overdue (Days)':    p.overdueDays ?? '',
      'Expected Close':    this.formatDate(p.expectedCloseDate),
      'Actual Close':      this.formatDate(p.actualCloseDate),
      // No 'Active' column — this page lists active pursuits only, so it would always read "Yes".
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb: XLSX.WorkBook = { Sheets: { 'Presales Details': ws }, SheetNames: ['Presales Details'] };
    const buffer: unknown = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer as ArrayBuffer], { type: 'application/octet-stream' });
    const d = new Date();
    const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    saveAs(blob, `Presales_Details_${stamp}.xlsx`);
  }

  clearFilters(): void {
    this.selectedProjectStatus = null;
    this.selectedStage = null;
    this.selectedProgress = null;
    this.selectedPMId = null;
    this.selectedClientId = null;
    this.selectedAccountManagerId = null;
    this.selectedServiceGroupId = null;
    this.pageHeaderService.setSearchText('');
    this.applyFilters();
  }

  setViewMode(mode: ViewMode): void {
    this.viewMode = mode;
    if (mode === 'kanban') this.rebuildKanbanColumns();
  }

  /**
   * Buckets the currently filtered projects into one column per progress state —
   * the working status a PM moves a pursuit through (To Start → In Progress →
   * On Hold / Closed). Sales stage is not the column axis here: it is where the
   * deal sits commercially, and it is shown on the card itself so both readings
   * are available at once. Deals with no progress set land in a trailing
   * "Not set" column rather than vanishing from the board.
   *
   * Within a column, deals are ordered by value so the biggest ones are seen
   * first without scrolling.
   */
  private rebuildKanbanColumns(): void {
    const columns: KanbanColumn[] = PRESALES_PROGRESS_OPTIONS.map(o => ({
      key: o.value,
      label: o.label,
      cssClass: 'progress-' + o.value.toLowerCase().replace(/\s+/g, '-'),
      projects: [],
    }));
    const unset: KanbanColumn = { key: '', label: 'Not Set', cssClass: 'progress-unset', projects: [] };

    for (const p of this.filteredProjects) {
      const col = columns.find(c => c.key === p.progress) ?? unset;
      col.projects.push(p);
    }

    // Sort by USD value where a rate exists so columns are comparable across
    // currencies; unrated deals sort last rather than being treated as zero.
    const weight = (p: PresalesProjectRow) =>
      toUsd(p.revenueTarget ?? 0, p.currency, this.rateMap) ?? -1;
    for (const c of columns) c.projects.sort((a, b) => weight(b) - weight(a));
    unset.projects.sort((a, b) => weight(b) - weight(a));

    // The catch-all only earns a column when something is actually in it.
    this.kanbanColumns = unset.projects.length ? [...columns, unset] : columns;
  }

  /** Total USD value of a column, for the header subtotal. Null when no rates loaded. */
  columnUsdTotal(col: KanbanColumn): number | null {
    if (!this.hasRates) return null;
    let total = 0;
    for (const p of col.projects) {
      const usd = toUsd(p.revenueTarget ?? 0, p.currency, this.rateMap);
      if (usd != null) total += usd;
    }
    return Math.round(total);
  }

  // ================= Modal =================

  openDetails(project: PresalesProject): void {
    this.selectedProject = project;
    this.activeTab = 'overview';
    this.editMode = false;
    this.editModel = null;
    this.displayDialog = true;
    // Open directly in edit mode for users who can manage; read-only otherwise.
    if (this.canManage) {
      this.startEdit();
    }
  }

  /**
   * Hands the open project to the Presales Inventory, which owns the fuller pursuit form —
   * client, presales cost center and the sales-stage transitions this dialog's Overview tab
   * does not carry.
   *
   * The dialog is closed first: routing away unmounts this screen regardless, so leaving it
   * open would only strand its state. An in-flight save blocks the jump rather than racing
   * it, and an unsaved inline edit is discarded the same way Cancel discards it.
   */
  openInInventory(): void {
    if (this.saveInFlight || !this.selectedProject) return;

    const projectId = this.selectedProject.id;
    if (this.editMode) {
      this.cancelEdit();
    }
    this.hideDialog();

    this.router.navigate(['/presales/inventory'], {
      queryParams: { projectId, openForm: 1 }
    });
  }

  hideDialog(): void {
    // Don't lose an in-flight save; and if editing, confirm-free cancel is fine
    // since Cancel already discards. Just guard the in-flight case.
    if (this.saveInFlight) return;
    this.displayDialog = false;
    this.selectedProject = null;
    this.editMode = false;
    this.editModel = null;
  }

  setTab(tab: typeof this.activeTab): void {
    // Leaving Overview while editing discards the edit (matches Cancel).
    if (tab !== 'overview' && this.editMode) this.cancelEdit();
    this.activeTab = tab;
  }

  // ================= Inline edit (Overview) =================

  /** Enter edit mode with a working copy so Cancel can discard cleanly. */
  startEdit(): void {
    if (!this.canManage || !this.selectedProject) return;
    const copy = { ...this.selectedProject };
    // <input type="date"> binds to yyyy-MM-dd; trim any time component.
    copy.expectedCloseDate = this.toDateInput(copy.expectedCloseDate);
    copy.startDate = this.toDateInput(copy.startDate) ?? '';
    copy.endDate = this.toDateInput(copy.endDate);
    this.editModel = copy;
    this.editMode = true;
  }

  /** Normalize an ISO date/datetime string to yyyy-MM-dd for <input type="date">. */
  private toDateInput(iso?: string | null): string | null {
    if (!iso) return null;
    return iso.length >= 10 ? iso.substring(0, 10) : iso;
  }

  cancelEdit(): void {
    if (this.saveInFlight) return;
    this.editMode = false;
    this.editModel = null;
  }

  /** Persist the working copy via the standard update endpoint. */
  saveEdit(): void {
    if (this.saveInFlight || !this.editModel || !this.selectedProject) return;
    const m = this.editModel;
    if (!m.name?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Name required', detail: 'Project name cannot be empty.' });
      return;
    }
    // StartDate is a required (non-nullable) field on the backend — block an empty save early.
    if (!m.startDate) {
      this.messageService.add({ severity: 'warn', summary: 'Start date required', detail: 'Start date cannot be empty.' });
      return;
    }

    // Send the full DTO the update endpoint expects. FK/id fields are carried
    // through unchanged from the loaded project (they aren't edited here).
    const dto: CreatePresalesProject = {
      projectCode:       m.projectCode?.trim() || undefined,
      name:              m.name.trim(),
      managerId:         m.managerId,
      accountManagerId:  m.accountManagerId || undefined,
      presalesClientId:  m.presalesClientId || undefined,
      serviceGroupId:    m.serviceGroupId || undefined,
      costCenterId:      m.costCenterId || undefined,
      revenueTarget:     m.revenueTarget ?? 0,
      currency:          m.currency,
      winningPercentage: Math.max(0, Math.min(100, m.winningPercentage ?? 0)),
      salesStage:        m.salesStage || 'Lead',
      progress:          m.progress || undefined,
      projectStatus:     m.projectStatus || undefined,
      plannedEstimateDays: m.plannedEstimateDays ?? undefined,
      // No UI on this page any more (consolidated into Comments), but carried through
      // so a save here doesn't null a value still editable on the Inventory page.
      description:       m.description || undefined,
      scopeSummary:      m.scopeSummary || undefined,
      // No UI for this yet — carried through so a save doesn't null a value set elsewhere.
      teamMembers:       m.teamMembers || undefined,
      comments:          m.comments || undefined,
      leadSource:        m.leadSource || undefined,
      competitor:        m.competitor || undefined,
      lostReason:        m.salesStage === 'Lost' ? (m.lostReason || undefined) : undefined,
      startDate:         m.startDate,
      endDate:           m.endDate || undefined,
      expectedCloseDate: m.expectedCloseDate || undefined,
      isActive:          m.isActive,
    };

    this.saveInFlight = true;
    this.presalesService.updateProject(m.id, dto)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this.saveInFlight = false;
          this.selectedProject = saved;
          // Reflect the change in the underlying list without a full reload.
          // Overdue is re-derived here so an edited end date updates the column.
          const savedRow: PresalesProjectRow = { ...saved, overdueDays: this.calcOverdueDays(saved.endDate) };
          const idx = this.projects.findIndex(p => p.id === saved.id);
          if (idx >= 0) this.projects[idx] = savedRow;
          this.applyFilters();
          this.editMode = false;
          this.editModel = null;
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: `"${saved.name}" updated` });
        },
        error: (err) => {
          this.saveInFlight = false;
          const detail = (err?.error?.details || err?.error?.detail || err?.error?.message || err?.message) ?? 'Failed to save changes';
          this.messageService.add({ severity: 'error', summary: 'Could not save', detail });
        }
      });
  }

  // ================= Effort =================

  /**
   * Actual Effort (Days) — displayed as a whole number, no decimals.
   * Computed by the backend from the twin project's timesheet hours, so it is populated at
   * every sales stage. Always read from selectedProject: editModel is a working copy the user
   * can't change this value on, and it would go stale mid-edit.
   */
  getActualEffortDisplay(): string {
    const val = this.selectedProject?.actualEffortDays;
    if (val == null || isNaN(val)) return '0';
    return Math.round(val).toString();
  }

  /**
   * Remaining Effort (Days) = Planned - Actual, recomputed client-side so it updates live
   * while a planned estimate is being typed. Falls back to the saved value outside edit mode.
   */
  getRemainingEffort(): string {
    const planned = this.editMode
      ? this.editModel?.plannedEstimateDays
      : this.selectedProject?.plannedEstimateDays;

    if (planned == null || isNaN(planned)) return '0';

    const actual = this.selectedProject?.actualEffortDays || 0;
    return Math.round(planned - actual).toString();
  }

  // ================= Helpers =================

  /**
   * Stage class for a Kanban card. The columns now group by progress, so the
   * card's coloured left edge carries the sales stage instead — the two readings
   * stay visible at once without a second badge row.
   */
  stageClass(stage?: SalesStage | null): string {
    return 'stage-' + (stage || 'unset').toLowerCase().replace(/\s+/g, '-');
  }

  stageSeverity(stage: SalesStage): 'info' | 'success' | 'warn' | 'secondary' | 'danger' {
    switch (stage) {
      case 'Won':         return 'success';
      case 'Lost':        return 'danger';
      case 'Negotiation': return 'warn';
      case 'Proposal':    return 'info';
      case 'Qualified':   return 'info';
      case 'Lead':        return 'secondary';
      default:            return 'secondary';
    }
  }

  /** Badge colour for RAG health. Matches getStatusSeverity in ProjectDetailsComponent. */
  projectStatusSeverity(status?: PresalesProjectStatus | string | null): 'success' | 'warn' | 'danger' | 'secondary' {
    switch ((status || '').toLowerCase()) {
      case 'green': return 'success';
      case 'amber': return 'warn';
      case 'red':   return 'danger';
      default:      return 'secondary';
    }
  }

  /**
   * Header-tint class for a grid card. Mirrors getStatusIconClass in
   * ProjectsListComponent so both grids tint their card headers identically.
   */
  gridStatusClass(status?: PresalesProjectStatus | string | null): string {
    switch ((status || '').toLowerCase()) {
      case 'green': return 'status-green';
      case 'amber': return 'status-amber';
      case 'red':   return 'status-red';
      default:      return 'status-default';
    }
  }

  /**
   * Pill class for the RAG status table cell. Mirrors getStatusBadgeClass in
   * ProjectsListComponent — both resolve to the shared rules in styles/_badges.scss
   * so the Status column reads identically on the Project List and this page.
   */
  projectStatusBadgeClass(status?: PresalesProjectStatus | string | null): string {
    switch ((status || '').toLowerCase()) {
      case 'green': return 'status-badge-green';
      case 'amber': return 'status-badge-amber';
      case 'red':   return 'status-badge-red';
      default:      return 'status-badge-default';
    }
  }

  /**
   * Badge colour for pursuit progress. The 'on track' / 'off track' / 'blocker' cases
   * are retired from the dropdown but kept here so legacy rows still read in colour.
   */
  progressSeverity(progress?: PresalesProgress | string | null): 'info' | 'success' | 'warn' | 'secondary' | 'danger' {
    switch ((progress || '').toLowerCase()) {
      case 'inprogress': return 'info';
      case 'closed':     return 'success';
      case 'on hold':    return 'danger';
      case 'to start':   return 'secondary';
      case 'on track':   return 'success';
      case 'off track':  return 'warn';
      case 'blocker':    return 'danger';
      default:           return 'secondary';
    }
  }

  /**
   * Whole days the project is past its end date. Returns null when no end date is
   * set or the date has not passed, so the column reads "—" rather than "0 days".
   * Both sides are floored to midnight so a project due today is not counted late.
   */
  calcOverdueDays(endDate?: string | null): number | null {
    if (!endDate) return null;
    const end = new Date(endDate);
    if (isNaN(end.getTime())) return null;
    end.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.floor((today.getTime() - end.getTime()) / 86400000);
    return days > 0 ? days : null;
  }

  /**
   * "62 Days Overdue" / "1 Day Overdue". One helper so the table, grid card and
   * Kanban card can't drift into three different phrasings.
   */
  overdueLabel(days: number): string {
    return `${days} ${days === 1 ? 'Day' : 'Days'} Overdue`;
  }

  formatDate(iso?: string | null): string {
    if (!iso) return '—';
    return this.datePipe.transform(iso, 'MMM d, y') ?? '—';
  }

  formatMoney(n: number): string {
    return (n ?? 0).toLocaleString('en-US');
  }

  /**
   * Abbreviated money for Kanban cards — "1.2M" / "450K" / "900". A card is
   * ~272px wide and shares its top row with the code, so a full 10-digit LKR
   * figure would wrap or truncate. The exact value is in the details modal.
   */
  formatMoneyCompact(n: number): string {
    const v = n ?? 0;
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return (v / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1).replace(/\.0$/, '') + 'M';
    if (abs >= 1_000)     return (v / 1_000).toFixed(abs >= 10_000 ? 0 : 1).replace(/\.0$/, '') + 'K';
    return v.toLocaleString('en-US');
  }

  /** Up to two initials for a card avatar; '?' when the name is missing. */
  initials(name?: string | null): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  }

  /**
   * Deterministic avatar tint from a name, so the same person keeps the same
   * colour across cards. Six hues, wide apart, all readable on the card surface.
   */
  avatarHue(name?: string | null): number {
    const s = name || '';
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
    return [210, 150, 275, 25, 340, 190][h % 6];
  }

  /**
   * Days until the expected close date — negative once past. Null when unset,
   * so the card can fall back to the overdue/absolute-date reading instead.
   */
  daysToClose(iso?: string | null): number | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - today.getTime()) / 86400000);
  }

  /** "Closes in 5d" / "Closes today" / "Closed 3d ago" — short enough for a card foot. */
  closeLabel(iso?: string | null): string {
    const d = this.daysToClose(iso);
    if (d == null) return '';
    if (d === 0) return 'Closes today';
    if (d > 0)   return d > 60 ? this.formatDate(iso) : `Closes in ${d}d`;
    return this.formatDate(iso);
  }

  /** True when the expected close is inside the next 14 days — worth highlighting. */
  isClosingSoon(iso?: string | null): boolean {
    const d = this.daysToClose(iso);
    return d != null && d >= 0 && d <= 14;
  }

  // ================= Stats (mirrors Inventory) =================
  //
  // KPI cards show a single USD figure so numbers are comparable. Rows keep
  // native currency. Unrated currencies are excluded and counted so the total
  // is never silently understated.

  private get openProjects(): PresalesProject[] {
    return this.projects.filter(p => p.salesStage !== 'Won' && p.salesStage !== 'Lost');
  }

  /** Active pipeline value in USD. */
  get usdPipeline(): number {
    let total = 0;
    for (const p of this.openProjects) {
      const usd = toUsd(p.revenueTarget, p.currency, this.rateMap);
      if (usd != null) total += usd;
    }
    return Math.round(total);
  }

  /** Weighted pipeline in USD. */
  get usdWeighted(): number {
    let total = 0;
    for (const p of this.openProjects) {
      const usd = toUsd((p.revenueTarget ?? 0) * (p.winningPercentage ?? 0) / 100, p.currency, this.rateMap);
      if (usd != null) total += usd;
    }
    return Math.round(total);
  }

  /** Count of open deals whose currency has no rate. */
  get unratedDealCount(): number {
    if (this.rateMap.size === 0) return 0;
    return this.openProjects.filter(p => !this.rateMap.has(p.currency)).length;
  }

  get hasRates(): boolean { return this.rateMap.size > 0; }

  /**
   * Deal counts behind the KPI cards. Surfaced on the cards themselves so a
   * headline figure derived from one or two deals is not read with the same
   * confidence as one derived from fifty.
   */
  get openDealCount(): number { return this.openProjects.length; }

  /** Open deals actually included in the USD figures (i.e. with a known FX rate). */
  get ratedDealCount(): number {
    return this.openProjects.filter(p => this.rateMap.has(p.currency)).length;
  }

  get closedDealCount(): number { return this.wonCount + this.lostCount; }

  /** Average open-deal size in USD = usdPipeline / open deals with a known rate. */
  get usdAvgDealSize(): number {
    const rated = this.openProjects.filter(p => this.rateMap.has(p.currency)).length;
    if (rated === 0) return 0;
    return Math.round(this.usdPipeline / rated);
  }

  /** Open deals whose Expected Close date falls in the current calendar month. */
  get closingThisMonthCount(): number {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    return this.openProjects.filter(p => {
      if (!p.expectedCloseDate) return false;
      const d = new Date(p.expectedCloseDate);
      return d.getFullYear() === y && d.getMonth() === m;
    }).length;
  }

  get wonCount(): number { return this.projects.filter(p => p.salesStage === 'Won').length; }
  get lostCount(): number { return this.projects.filter(p => p.salesStage === 'Lost').length; }

  get winRatePct(): number {
    const closed = this.wonCount + this.lostCount;
    if (closed === 0) return 0;
    return Math.round((this.wonCount / closed) * 100);
  }

  isWon(): boolean {
    return this.selectedProject?.salesStage === 'Won';
  }

  isAlreadyPromoted(): boolean {
    return !!this.selectedProject?.promotedToProjectId;
  }

  // ================= Promote-to-Delivery flow =================

  /** Opens the promote wizard + lazy-loads the main Clients list (for the "link existing" option). */
  openPromoteDialog(): void {
    if (!this.selectedProject) return;

    // Default to Create-new when a presales client exists, otherwise force Link mode.
    this.promoteMode = this.selectedProject.presalesClientName ? 'create' : 'link';
    this.promoteExistingClientId = null;
    // Pre-fill delivery name from the presales project — saves a copy-paste
    // for the common case. User can edit before Promote.
    this.promoteProjectNameOverride = this.selectedProject.name;
    this.promoteProjectCodeOverride = '';
    this.promoteInFlight = false;
    this.promoteStep = 1;
    this.displayPromoteDialog = true;

    // Refresh main Clients every open — new clients added elsewhere won't be
    // missed in the "Link existing" dropdown.
    this.clientService.getAllClients()
      .pipe(takeUntilDestroyed(this.destroyRef), catchError(() => of([] as Client[])))
      .subscribe(list => this.mainClients = (list || []).filter(c => c.isActive));
  }

  closePromoteDialog(): void {
    if (this.promoteInFlight) return;
    this.displayPromoteDialog = false;
  }

  /** Whether the user can advance from the current step. */
  canGoNextPromoteStep(): boolean {
    if (!this.selectedProject) return false;
    if (this.promoteStep === 1) {
      if (this.promoteMode === 'create') return !!this.selectedProject.presalesClientName;
      if (this.promoteMode === 'link')   return !!this.promoteExistingClientId;
    }
    return true;
  }

  /** Whether the wizard can commit (review step + valid state). */
  canConfirmPromote(): boolean {
    if (!this.selectedProject || this.promoteInFlight) return false;
    if (this.promoteMode === 'link' && !this.promoteExistingClientId) return false;
    if (this.promoteMode === 'create' && !this.selectedProject.presalesClientName) return false;
    return true;
  }

  nextPromoteStep(): void {
    if (!this.canGoNextPromoteStep()) return;
    if (this.promoteStep < 3) this.promoteStep = (this.promoteStep + 1) as 1 | 2 | 3;
  }

  backPromoteStep(): void {
    if (this.promoteStep > 1) this.promoteStep = (this.promoteStep - 1) as 1 | 2 | 3;
  }

  /** Used by the Promote wizard's Review step to name the linked main-Clients row. */
  getMainClientName(clientId: string | null): string {
    if (!clientId) return '—';
    return this.mainClients.find(c => c.id === clientId)?.name ?? '—';
  }

  /** Fires POST /api/PresalesProject/{id}/promote — atomic backend transaction. */
  confirmPromote(): void {
    if (!this.selectedProject || this.promoteInFlight) return;

    if (this.promoteMode === 'link' && !this.promoteExistingClientId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Choose a client',
        detail: 'Pick an existing client from the dropdown, or switch to "Create new client".'
      });
      return;
    }

    const req: PromoteToDeliveryRequest = {
      createNewClient: this.promoteMode === 'create',
      existingClientId: this.promoteMode === 'link' ? this.promoteExistingClientId : null,
      deliveryProjectName: (this.promoteProjectNameOverride || '').trim() || undefined,
      deliveryProjectCode: (this.promoteProjectCodeOverride || '').trim() || undefined,
    };

    this.promoteInFlight = true;
    this.presalesService.promoteProject(this.selectedProject.id, req)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.promoteInFlight = false;

          // Reflect the promotion in the loaded list so subsequent UI matches server state.
          // Update via immutable copy — mutating `selectedProject` directly leaks a
          // half-updated reference into `projects` before applyFilters copies it.
          if (this.selectedProject) {
            const promotedId = result.promotedToProjectId;
            const targetId = this.selectedProject.id;
            const idx = this.projects.findIndex(p => p.id === targetId);
            if (idx >= 0) {
              this.projects[idx] = { ...this.projects[idx], promotedToProjectId: promotedId };
            }
            this.selectedProject = { ...this.selectedProject, promotedToProjectId: promotedId };
            this.applyFilters();
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Promoted to delivery',
            detail: result.message,
            life: 6000
          });

          this.closePromoteDialog();
        },
        error: (err) => {
          this.promoteInFlight = false;
          const detail = err?.error?.details || err?.error?.message || err?.message || 'Failed to promote to delivery.';
          this.messageService.add({
            severity: 'error',
            summary: 'Promote failed',
            detail,
            life: 8000
          });
        }
      });
  }
}
